import {getSafely, type TestIdProps} from 'shared/web';

import type {
  ActivePreset,
  GetDataQueryResponse,
  TransformedDefinitionApiResponse,
} from '../types/Api';
import type {EagColumn} from '../types/EagColumn';
import type {EagColumnGroup} from '../types/EagColumnGroup';
import {columnToBasicDefs} from './columnToBasicDefs';
import {columnToFilterDefs} from './columnToFilterDefs';
import {columnToTypeDefs} from './columnToTypeDefs';
import {isEagColumnGroup} from './isEagColumnGroup';

/** Runtime sort entry returned by the API but not yet added to the public preset types. */
type PresetSortEntry = {column: string; order: 'ASC' | 'DESC'};

/** Extends the canonical `ActivePreset` union to include the `sort` array that the
 *  API returns at runtime but that is not yet reflected in `Api.ts`. */
type ActivePresetWithSort = ActivePreset & {sort?: PresetSortEntry[]};

export type EagColumnOrGroup = EagColumn | EagColumnGroup;

export type TransformDefinitionProps = TestIdProps & {
  definition: TransformedDefinitionApiResponse;
  columnOverrides?: Record<string, Partial<EagColumn>>;
  activePreset?: ActivePresetWithSort;
  dataQuery: GetDataQueryResponse;
};

export const transformDefinition = (props: TransformDefinitionProps): EagColumnOrGroup[] => {
  const definition = props.definition;

  if (!definition) {
    return [];
  }

  const columns = definition.columns;
  const groups = definition.groups;
  const activePreset = props.activePreset;
  const columnOverrides = props.columnOverrides;

  // O(n) index Maps — replaces O(n²) .findIndex()/.find() in the sort comparator and forEach body.
  const presetColIndexMap = new Map<string, number>();
  const presetColMap = new Map<string, ActivePresetWithSort['columnsSettings'][number]>();
  if (activePreset?.columnsSettings) {
    activePreset.columnsSettings.forEach((col, i) => {
      presetColIndexMap.set(col.columnKey, i);
      presetColMap.set(col.columnKey, col);
    });
  }

  const presetSortMap = new Map<string, {entry: PresetSortEntry; index: number}>();
  if (activePreset?.sort) {
    activePreset.sort.forEach((sort, i) => {
      presetSortMap.set(sort.column, {entry: sort, index: i});
    });
  }

  const colDefsArr: EagColumnOrGroup[] = [];

  columns
    .toSorted((a, b) => {
      if (!activePreset) {
        return 0;
      }
      const aIndex = presetColIndexMap.get(a.key) ?? -1;
      const bIndex = presetColIndexMap.get(b.key) ?? -1;
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    })
    .forEach((column) => {
      const colOverride = (columnOverrides ?? {})[column.key];
      const defaultPresetCol = presetColMap.get(column.key);

      const sortData = presetSortMap.get(column.key);
      const defaultPresetSort = sortData?.entry;
      const defaultPresetSortIndex = sortData ? sortData.index : -1;

      const colDef: EagColumn = {
        ...columnToBasicDefs(
          props.dataQuery,
          definition,
          column,
          defaultPresetCol,
          defaultPresetSort,
          defaultPresetSortIndex === -1 ? undefined : defaultPresetSortIndex
        ),
        ...columnToTypeDefs(column),
        ...columnToFilterDefs(column),
        ...(colOverride ?? {}),
      } as EagColumn;

      if (!column.group || !groups) {
        colDefsArr.push(colDef);
      } else {
        const group = getSafely(groups, column.group);

        if (!group) {
          colDefsArr.push(colDef);
          return;
        }

        const lastItemIndex = colDefsArr.length - 1;
        const lastItem = colDefsArr[lastItemIndex];

        if (lastItem && isEagColumnGroup(lastItem) && lastItem.groupId === column.group) {
          colDefsArr[lastItemIndex] = {
            ...lastItem,
            children: [...lastItem.children, colDef],
          };
        } else {
          colDefsArr.push({
            ...group,
            children: [colDef],
          });
        }
      }
    });

  return colDefsArr;
};
